import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'utilisateur'
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { access_token, user } = response.data;
      
      login(user, access_token);
      toast.success('Connexion réussie!');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Erreur de connexion. Vérifiez vos identifiants.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, registerData);
      toast.success('Compte créé avec succès!');
      setIsRegister(false);
      setFormData({ email: registerData.email, password: '' });
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <div className="w-full max-w-md">
        <Card className="shadow-xl">
          <CardHeader className="text-center pb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center mb-6 shadow-lg">
              <span className="text-white font-bold text-xl">DSI</span>
            </div>
            
            <CardTitle className="text-2xl font-bold text-secondary">
              {isRegister ? 'Créer un compte' : 'Gestion de contrats'}
            </CardTitle>
            <CardDescription className="text-text-muted mt-2">
              {isRegister ? 'Créez votre compte pour accéder au système' : 'Connectez-vous à votre espace de gestion'}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {!isRegister ? (
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="form-label">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nom.prenom@entreprise.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="login-email-input"
                    className="form-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="password" className="form-label">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    data-testid="login-password-input"
                    className="form-input"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="w-full btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner mr-2"></div>
                      Connexion...
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
                
                <div className="text-center">
                  <Button 
                    type="button"
                    variant="ghost"
                    onClick={() => setIsRegister(true)}
                    className="btn-ghost text-primary"
                    data-testid="switch-to-register-button"
                  >
                    Pas encore de compte ? S'inscrire
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-6">
                <div>
                  <Label htmlFor="full_name" className="form-label">Nom complet</Label>
                  <Input
                    id="full_name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={registerData.full_name}
                    onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})}
                    required
                    data-testid="register-name-input"
                    className="form-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="register_email" className="form-label">Email</Label>
                  <Input
                    id="register_email"
                    type="email"
                    placeholder="nom.prenom@entreprise.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    required
                    data-testid="register-email-input"
                    className="form-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="register_password" className="form-label">Mot de passe</Label>
                  <Input
                    id="register_password"
                    type="password"
                    placeholder="••••••••"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    required
                    data-testid="register-password-input"
                    className="form-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="role" className="form-label">Rôle</Label>
                  <select
                    id="role"
                    value={registerData.role}
                    onChange={(e) => setRegisterData({...registerData, role: e.target.value})}
                    data-testid="register-role-select"
                    className="form-select"
                  >
                    <option value="utilisateur">Utilisateur</option>
                    <option value="gestionnaire">Gestionnaire</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  data-testid="register-submit-button"
                  className="w-full btn-secondary"
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner mr-2"></div>
                      Création...
                    </>
                  ) : (
                    'Créer le compte'
                  )}
                </Button>
                
                <div className="text-center">
                  <Button 
                    type="button"
                    variant="ghost"
                    onClick={() => setIsRegister(false)}
                    className="btn-ghost text-primary"
                    data-testid="switch-to-login-button"
                  >
                    Déjà un compte ? Se connecter
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}